import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wand2, Loader2, Trash2, ScanSearch } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import EditableVisualBlocks, { countFlags } from './EditableVisualBlocks';
import { recheckVisuals } from './SavedVisualsEditor';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// Edit-mode control: NAVI proposes visual blocks for a section; admin previews and accepts.
export default function EnhanceVisualsButton({ courseId, sectionIndex, token, hasBlocks, onSaved }) {
  const [checking, setChecking] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const url = `${API}/ai/course/${courseId}/sections/${sectionIndex}`;

  const propose = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${url}/enhance-visuals`, {}, { headers });
      setProposal(res.data.blocks);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'NAVI could not create visuals');
    } finally {
      setBusy(false);
    }
  };

  const save = async (blocks) => {
    setBusy(true);
    try {
      const res = await axios.put(`${url}/visual-blocks`, { blocks }, { headers });
      onSaved(res.data.blocks, res.data.visual_review);
      setProposal(null);
      toast.success(blocks.length ? 'Visuals saved. Review them below, then Mark reviewed to show learners' : 'Visuals removed');
    } catch (e) {
      toast.error('Could not save visuals');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 my-4">
      <Button type="button" size="sm" onClick={propose} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid={`enhance-visuals-btn-${sectionIndex}`}>
        {busy && !proposal ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />}
        {hasBlocks ? 'Regenerate visuals' : 'Enhance with visuals'}
      </Button>
      {hasBlocks && (
        <Button type="button" size="sm" variant="outline" onClick={() => save([])} disabled={busy} className="border-zinc-700 text-zinc-300" data-testid={`remove-visuals-btn-${sectionIndex}`}>
          <Trash2 className="h-4 w-4 mr-1.5" />Remove visuals
        </Button>
      )}
      <Dialog open={!!proposal} onOpenChange={(o) => !o && setProposal(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="visuals-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">NAVI's suggested visuals</DialogTitle>
            <DialogDescription className="text-zinc-400">NAVI only uses facts from this section and fact-checks itself. Edit anything inline, then accept. Learners won't see the visuals until you mark them reviewed.</DialogDescription>
          </DialogHeader>
          <EditableVisualBlocks blocks={proposal} onChange={setProposal} testIdPrefix="visual-preview" />
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={checking || !proposal?.length} onClick={async () => {
              setChecking(true);
              try { const b = await recheckVisuals(courseId, sectionIndex, proposal, token); setProposal(b); toast[countFlags(b) ? 'warning' : 'success'](countFlags(b) ? `${countFlags(b)} item(s) still not found in the section text` : 'NAVI found every item in the section text'); }
              catch (e) { toast.error(e.response?.data?.detail || 'NAVI could not fact-check right now'); }
              finally { setChecking(false); }
            }} className="border-zinc-700 text-zinc-300 sm:mr-auto" data-testid="visuals-preview-recheck-btn">
              {checking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ScanSearch className="h-4 w-4 mr-1.5" />}Re-check with NAVI
            </Button>
            <Button variant="outline" onClick={() => setProposal(null)} className="border-zinc-700 text-zinc-300" data-testid="visuals-discard-btn">Discard</Button>
            <Button onClick={() => save(proposal)} disabled={busy} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="visuals-accept-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Accept visuals
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
